import {
  Stack,
  Divider,
  Group,
  ActionIcon,
  Paper,
  Textarea,
  LoadingOverlay,
} from "@mantine/core"
import { IconPaperclip, IconSend, IconX } from "@tabler/icons-react"
import { useDebouncedCallback } from "@mantine/hooks"
import { ChatMessagePaper } from "./ChatMessagePaper"
import dayjs from "dayjs"
import { ChatHeaderGroup } from "./ChatHeaderGroup"
import { MessageListItem } from "./MessageListItem"
import { useBreakpoints } from "../../../utils/use-breakpoints"
import { useTranslation } from "react-i18next"
import { ChatType } from "../../../orval/gen/model"
import { FilePreview } from "../../../-components/FilePreview"
import { DefaultInfiniteList } from "../../../-components/InfiniteList"
import { SuspenseWithErrorBoundary } from "../../../-components/suspense"
import { EmojiPickerDropdown } from "../../app/-components/EmojiPickerDropdown.tsx"
import { DeviceType, useDeviceType } from "../../../utils/useDeviceType.ts"
import { DefaultError } from "../../../-components/error-context/index.tsx"
import { useDefaultSuspenseInfiniteQuery } from "../../../utils/useDefaultSuspenseInfiniteQuery.ts"
import { notifications } from "@mantine/notifications"
import { useGetAuthenticatedUser } from "../../app/-components/use-authenticated-user.tsx"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage.tsx"
import { showErrorNotification } from "@/utils/form-submission-notifications.ts"
import { useChatPostAttachmentApi } from "@/attachment-api/chatPostAttachmentApi.tsx"
import { useDefaultFileDialog } from "../useDefaultFileDialog.tsx"
import {
  getChatPosts,
  getGetChatPostsQueryKey,
  useCreateChatPost,
  useGetChatByIdSuspense,
  useUpdateReadMessage,
} from "@/orval/gen"
import { ChatDto, ChatPostDto } from "@/orval/gen/model/index.ts"
import { debugLog } from "@/utils/debugLog.tsx"

interface Props {
  chatId: number
}

export function Chat({ chatId }: Props) {
  const { data: chat } = useGetChatByIdSuspense({
    chatId,
  })

  if (!chat) throw new DefaultError({ title: "Something went wrong." })

  const [message, setMessage] = useUserLocalStorage({
    key: "chat-" + chatId + "-message",
    defaultValue: "",
    sync: false,
  })

  const [replyToMessage, setReplyToMessage] =
    useUserLocalStorage<null | ChatPostDto>({
      key: "chat-" + chatId + "-reply",
      defaultValue: null,
      sync: false,
    })

  function resetMessage() {
    setMessage("")
    setReplyToMessage(null)
    setFiles(null)
  }

  const { mutateAsync: sendMessage, isPending } = useCreateChatPost({
    mutation: {
      onSuccess: async (chatPostId) => {
        if (files) {
          files.forEach(async (file) => {
            try {
              await chatPostAttchmentApi.addAttachmentAndMetadata(
                file,
                {
                  chatPostId,
                },
                chatPostId
              )
            } catch (err) {
              console.error(err)
              if (err as DefaultError) {
                const error = err as DefaultError
                showErrorNotification(error.message)
              }
            }
          })
          setFiles(null)
        }

        resetMessage()
      },
      onError: showErrorNotification,
    },
  })

  const chatPostAttchmentApi = useChatPostAttachmentApi()
  const handleSendMessage = async () => {
    debugLog("handleSendMessage", { message, files })
    if (message.length > 0 || (files && files?.length > 0)) {
      await sendMessage({
        data: {
          chatId: chat.chatId,
          replyToMessageId: replyToMessage?.chatPostId,
          message,
        },
      })
    }
  }

  const { xs, sm, md } = useBreakpoints()

  const isSmallLayout = xs || sm || md

  const textareaRef = React.useRef<HTMLTextAreaElement>(null)

  const device = useDeviceType()

  const currentUserId = useGetAuthenticatedUser().data.userId
  const chatMember = chat.chatMembers.find((cm) => cm.userId === currentUserId)
  const readOnly =
    (chat.chatType === "Broadcast" || chat.isReadOnly) && !chatMember?.isAdmin

  React.useEffect(() => {
    device === DeviceType.Desktop && textareaRef.current?.focus()
    setFiles(null)
  }, [chatId])

  const { files, fileDialog, setFiles } = useDefaultFileDialog({
    maxSize: 10,
    multiple: true,
  })

  return (
    <Stack key={"stack-" + chatId} h="100%" w="100%" gap={0} flex="1">
      {!isSmallLayout && (
        //without the key the update modal in the header group doesnt update state
        <React.Fragment key={"header-" + chatId}>
          <ChatHeaderGroup chatId={chatId} />
          <Divider />
        </React.Fragment>
      )}
      <Paper mih={0} flex="1">
        <SuspenseWithErrorBoundary>
          <ChatList
            currentUserId={currentUserId}
            chat={chat}
            readOnly={readOnly}
            onReplyButtonClick={(message) => {
              textareaRef.current?.focus()
              setReplyToMessage(message)
            }}
          />
        </SuspenseWithErrorBoundary>
      </Paper>
      <Stack gap={5}>
        {replyToMessage ? (
          <Group pr="md">
            <ChatMessagePaper
              flex="1 0 0"
              showReadIndicator={
                chat.chatType !== "Broadcast" &&
                replyToMessage.userId === currentUserId
              }
              w="0"
              message={replyToMessage}
            />
            <ActionIcon
              style={{ pointerEvents: "all" }}
              onClick={() => setReplyToMessage(null)}
              variant="subtle"
            >
              <IconX />
            </ActionIcon>
          </Group>
        ) : null}
        <Group>
          {files?.length
            ? files?.map((x, index) => (
                <Group key={index}>
                  <FilePreview
                    mah={100}
                    maw={200}
                    withDownload={false}
                    src={URL.createObjectURL(x)}
                    fileName={x.name}
                  />
                  <Group>
                    <ActionIcon
                      style={{ pointerEvents: "all" }}
                      onClick={() => {
                        setFiles(files.filter((_, i) => i !== index))
                      }}
                    >
                      <IconX />
                    </ActionIcon>
                  </Group>
                </Group>
              ))
            : null}
        </Group>
        {!readOnly && (
          <Group gap={5}>
            <LoadingOverlay visible={isPending} />
            <Textarea
              leftSectionProps={{
                style: { alignItems: "end", paddingBottom: 1 },
              }}
              rightSectionProps={{
                style: { alignItems: "end", paddingBottom: 1 },
              }}
              leftSection={
                <EmojiPickerDropdown
                  position="top"
                  focusTargetRef={textareaRef}
                  actionIconProps={{
                    variant: "subtle",
                    style: {
                      pointerEvents: "all",
                    },
                  }}
                  onSelect={(emoji) => {
                    const cursorPosition =
                      textareaRef.current?.selectionStart ?? 0
                    setMessage((prev) => {
                      const start = prev.slice(0, cursorPosition)
                      const end = prev.slice(cursorPosition)
                      return start + emoji.emoji + end
                    })
                  }}
                />
              }
              rightSection={
                <ActionIcon
                  onClick={fileDialog.open}
                  variant="subtle"
                  style={{ pointerEvents: "all" }}
                >
                  <IconPaperclip />
                </ActionIcon>
              }
              ref={textareaRef}
              flex="1"
              autosize
              autoFocus={device === DeviceType.Desktop}
              maxRows={15}
              style={{ pointerEvents: "all" }}
              value={message}
              onChange={(evt) => setMessage(evt.currentTarget.value)}
              onKeyDown={(evt) => {
                if (
                  evt.key === "Enter" &&
                  !evt.shiftKey &&
                  (message.length || files?.length)
                ) {
                  evt.preventDefault()
                  handleSendMessage()
                }
              }}
              onPaste={(e) => {
                if (e.clipboardData.files.length) {
                  const fileObject = e.clipboardData.files[0]
                  setFiles([...(files ?? []), fileObject])
                }
              }}
            />
            {message.length > 0 || files?.length ? (
              <ActionIcon
                style={{ pointerEvents: "all" }}
                disabled={!(message.length || files?.length)}
                onClick={handleSendMessage}
              >
                <IconSend />
              </ActionIcon>
            ) : null}
          </Group>
        )}
      </Stack>
    </Stack>
  )
}

interface ChatListProps {
  chat: ChatDto
  onReplyButtonClick: (message: ChatPostDto) => void
  readOnly: boolean
  currentUserId: number
}
function ChatList({
  chat,
  onReplyButtonClick,
  readOnly,
  currentUserId,
}: ChatListProps) {
  const [t] = useTranslation("app_server")
  const { mutateAsync: sendLastReadMessageId } = useUpdateReadMessage()

  const highestMessageIdRef = React.useRef<number>(-1)

  const debouncedSend = useDebouncedCallback(async () => {
    if (highestMessageIdRef.current > 0) {
      await sendLastReadMessageId({
        data: {
          chatId: chat.chatId,
          messageId: highestMessageIdRef.current,
        },
      })
    }
  }, 200)

  const chatMessages = useDefaultSuspenseInfiniteQuery(
    getChatPosts,
    (pageRequest) => ({
      chatId: chat.chatId,
      ...pageRequest,
    }),
    { queryKey: getGetChatPostsQueryKey({ chatId: chat.chatId }) }
  )

  React.useEffect(() => {
    notifications.hide("new-message-" + chat.chatId)
  }, [chat.chatId, chatMessages.data])

  const handleMessageInView = (postId: number) => {
    if (postId > highestMessageIdRef.current) {
      highestMessageIdRef.current = postId
      debouncedSend()
    }
  }

  return (
    <DefaultInfiniteList
      style={{
        pointerEvents: "all",
        overflow: "auto",
      }}
      direction="column-reverse"
      startAtBottom
      scrollToBottom
      queryResult={chatMessages}
      divider={(prev, curr) => {
        const currentDate = dayjs(curr?.timeStamp)
        if (
          curr &&
          (prev === undefined ||
            dayjs(prev.timeStamp).dayOfYear() !== currentDate.dayOfYear())
        ) {
          return (
            <Divider
              px="sm"
              size="sm"
              labelPosition="center"
              label={
                currentDate.isToday()
                  ? t("Today")
                  : currentDate.add(1, "day").isToday()
                    ? t("Yesterday")
                    : currentDate.format(
                        currentDate.add(2, "day").isToday() ? "dddd" : "ddd L"
                      )
              }
            />
          )
        }
      }}
      renderItem={({ entity: message }) => (
        <MessageListItem
          showReadIndicator={
            chat.chatType !== "Broadcast" && message.userId === currentUserId
          }
          showUserName={chat.chatType !== ChatType.Direct}
          onInViewportChange={(inView) => {
            !message.isRead && inView && handleMessageInView(message.chatPostId)
          }}
          showAvatars={chat.chatType !== ChatType.Direct}
          key={message.chatPostId}
          message={message}
          readOnly={readOnly}
          onReplyButtonClick={() => onReplyButtonClick(message)}
        />
      )}
    />
  )
}
